/**
 * The bridge between wall-clock time and the app's interval scale.
 *
 * External systems date things in Unix seconds; the timeline counts seconds as
 * a 1-based ordinal from the era boundary. The offset between the two is a
 * fixed number, but it is *derived from the engine* rather than written down:
 * a hardcoded constant here and a change in the calendar there would disagree
 * silently, and every external record would land at the wrong moment.
 */

import { constructInterval } from "@/common/intervals";
import { HGInterval } from "@/common/types";
import { MapInterval } from "@/engine/engine";

let epochOrdinal: bigint | null = null;

/**
 * The second ordinal of 1970-01-01T00:00:00Z.
 *
 * Memoized rather than computed once at module load: nothing may call the
 * engine before `initModule()` resolves, and this module is imported long
 * before that happens.
 */
export function unixEpochOrdinal(): bigint {
  if (epochOrdinal === null) {
    epochOrdinal = MapInterval(constructInterval(1970n, "y"), "s").begin.n;
  }
  return epochOrdinal;
}

/**
 * Null below the epoch rather than a negative ordinal.
 *
 * BC ordinals mirror across the era boundary instead of continuing downwards,
 * so arithmetic that walks off the bottom does not produce a wrong date -- it
 * produces one in the wrong era. No external system this feature targets dates
 * anything before 1970, so refusing is both correct and sufficient.
 */
export function unixSecondsToOrdinal(unixSeconds: number): bigint | null {
  if (!Number.isFinite(unixSeconds) || unixSeconds < 0) return null;
  return unixEpochOrdinal() + BigInt(Math.floor(unixSeconds));
}

export function dateToOrdinal(date: Date): bigint | null {
  const ms = date.getTime();
  if (!Number.isFinite(ms)) return null;
  return unixSecondsToOrdinal(Math.floor(ms / 1000));
}

export function ordinalToUnixSeconds(ordinal: bigint): number | null {
  const unix = ordinal - unixEpochOrdinal();
  if (unix < 0n) return null;
  return Number(unix);
}

export function ordinalToDate(ordinal: bigint): Date | null {
  const unix = ordinalToUnixSeconds(ordinal);
  return unix === null ? null : new Date(unix * 1000);
}

/** A one-second interval at a given moment, the finest the scale goes. */
export function secondIntervalAt(ordinal: bigint): HGInterval {
  return constructInterval(ordinal, "s");
}
